import React, { RefObject, useEffect, useMemo } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import {
  activateKeepAwakeAsync,
  deactivateKeepAwake,
} from 'expo-keep-awake';
import {
  useIsFocused,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { AVPlaybackSource, AVPlaybackStatus, Video } from 'expo-av';
import { styled } from 'dripsy';
import { AppStateStatus, View } from 'react-native';
import { useModal } from 'react-native-modalfy';
import { useTranslation } from 'react-i18next';
import { when } from 'mobx';

import {
  SwitchSubtitlesLanguageType,
  FullScreenVideoPlayer,
} from '@components';
import {
  HubNavigatorNavigationPropType,
  HubNavigatorRoutePropType,
  screens,
} from '@navigation';
import { IS_MOBILE, IS_WEB, parseApiError, passIf } from '@utils';
import { modals, ModalStackParamsType } from '@modals';
import {
  AnalyticsService,
  AppStateService,
  ShareService,
  ToastService,
} from '@services';
import { useLink, useVar } from '@hooks';
import type { Tutorial } from '@models/Tutorials/TutorialModel';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { useTutorialsModel } from '@models/Tutorials/TutorialsModel';
import { useAppModel } from '@models/App/AppModel';
import { useAuthModel } from '@models/AuthModel';
import { IPlaylist } from './TutorialContent';
import type { Course } from '@models/Courses/CourseModel';

const Container = styled(View)({
  flex: 1,
  borderRadius: 12,
  overflow: 'hidden',
});

export interface ITutorialPlayerProps {
  tutorial: Tutorial;
  course: Course | undefined;
  playlist: IPlaylist;
  onPressTimestamp(): void;
  videoPlayerRef: RefObject<Video>;
  fullScreenVideoPlayerRef: RefObject<Video>;
}

type NavigationPropType = HubNavigatorNavigationPropType<'Tutorial'>;
type RoutePropType = HubNavigatorRoutePropType<'Tutorial'>;
type RouteExtraParamsType = Pick<
  RoutePropType['params'],
  'shouldPlay'
>;

export const TutorialPlayer = observer<ITutorialPlayerProps>(
  ({
    tutorial,
    course,
    playlist,
    onPressTimestamp,
    videoPlayerRef,
    fullScreenVideoPlayerRef,
  }) => {
    const playback = useVar({
      position: 0,
      duration: 0,
      isLoaded: false,
    });
    const viewer = useViewerModel();
    const tutorials = useTutorialsModel();
    const app = useAppModel();
    const auth = useAuthModel();
    const { t } = useTranslation();
    const { buildFullLink } = useLink();
    const { openModal } = useModal<ModalStackParamsType>();
    const nav = useNavigation<NavigationPropType>();
    const route = useRoute<RoutePropType>();
    const isFocused = useIsFocused();
    const isVisibleFullScreenPlayer =
      route.params.isInFullScreen ?? false;

    const store = useLocalObservable(() => ({
      isVisibleFullScreenPlayer,
      playerStatus: null as null | AVPlaybackStatus,
      didViewSaved: false,
      subtitlesLanguage: null as SwitchSubtitlesLanguageType,
      isShouldPlay: false,
      isPlaybackInitiated: false,

      switchFullScreen(status: AVPlaybackStatus) {
        this.isVisibleFullScreenPlayer =
          !this.isVisibleFullScreenPlayer;
        this.playerStatus = status;
      },

      markViewAsSaved() {
        this.didViewSaved = true;
      },

      setIsShouldPlay(value: boolean) {
        this.isShouldPlay = value;
      },

      initiatePlayback() {
        this.isPlaybackInitiated = true;
      },
    }));

    useEffect(() => {
      if (route.params.shouldPlay) {
        getActivePlayerRef()?.playAsync();
        onPlayStart();
      }

      fetchSubtitles(app.videoPlayerSettings.subtitlesLanguage);

      if (IS_WEB) {
        // On Web when user is closing tab the unmount event of useEffect will not be triggered
        window.addEventListener(
          'beforeunload',
          beforeUnloadOrUnmount,
        );

        return () => {
          beforeUnloadOrUnmount();
          window.removeEventListener(
            'beforeunload',
            beforeUnloadOrUnmount,
          );
        };
      }

      /**
       * In React Native we can't reliably detect when the app is fully closed.
       * We send an event when the app goes to background (potential closure)
       * and re-send the "content play" event when it becomes active again.
       * This ensures we capture necessary data, but may result in over-reporting
       * of "content play" events to maintain logical consistency.
       */
      function appStatusHandler(status: AppStateStatus) {
        if (status === AppStateService.appStates.ACTIVE) {
          trackContentPlayAnalytic();
        }
        if (status === AppStateService.appStates.BACKGROUND) {
          beforeUnloadOrUnmount();
        }
      }

      AppStateService.addChangeListener(appStatusHandler);

      return () => {
        beforeUnloadOrUnmount();
        AppStateService.removeChangeListener(appStatusHandler);
      };
    }, []);

    useEffect(() => {
      if (!isFocused) {
        videoPlayerRef.current?.pauseAsync();
        fullScreenVideoPlayerRef.current?.pauseAsync();
      }
    }, [isFocused]);

    useEffect(() => {
      if (store.isShouldPlay) {
        activateKeepAwakeAsync();
      } else {
        deactivateKeepAwakeAsync();
      }
    }, [store.isShouldPlay]);

    async function deactivateKeepAwakeAsync() {
      try {
        await deactivateKeepAwake();
      } catch (error) {
        // TODO: improve types
        const err = error as Error & { code?: string };
        // Catch error when we call deactivateKeepAwake without calling
        // activateKeepAwakeAsync before, in this case we do not have
        // KEEP_AWAKE_TAG yet
        if (err.code === 'ERR_KEEP_AWAKE_TAG_INVALID') {
          return;
        }

        throw error;
      }
    }

    function beforeUnloadOrUnmount() {
      if (playback.position > 0 && store.isPlaybackInitiated) {
        trackPercentagePlayedAnalytic();
        saveWatchTime();
      }
      deactivateKeepAwakeAsync();

      savePosition();
    }

    function getActivePlayerRef() {
      if (store.isVisibleFullScreenPlayer) {
        return fullScreenVideoPlayerRef.current;
      } else {
        return videoPlayerRef.current;
      }
    }

    function trackPercentagePlayedAnalytic() {
      const percentagePlayed =
        (playback.position / playback.duration) * 100;

      if (percentagePlayed >= 10 && percentagePlayed <= 80) {
        AnalyticsService.trackContentInterest({
          contentId: tutorial.id,
          contentTitle: tutorial.title,
          tagIds: tutorial.tags.ids,
          tagNames: tutorial.tags.names,
          ...(course ? { courseId: course.id } : {}),
        });
      } else if (percentagePlayed > 80) {
        AnalyticsService.trackContentWatch({
          contentId: tutorial.id,
          contentTitle: tutorial.title,
          tagIds: tutorial.tags.ids,
          tagNames: tutorial.tags.names,
          ...(course ? { courseId: course.id } : {}),
        });
      } else if (percentagePlayed < 10) {
        AnalyticsService.trackContentStart({
          contentId: tutorial.id,
          contentTitle: tutorial.title,
          tagIds: tutorial.tags.ids,
          tagNames: tutorial.tags.names,
          ...(course ? { courseId: course.id } : {}),
        });
      }
    }

    function trackContentPlayAnalytic() {
      AnalyticsService.trackContentPlay({
        contentId: tutorial.id,
        contentTitle: tutorial.title,
        tagIds: tutorial.tags.ids,
        tagNames: tutorial.tags.names,
        ...(course
          ? { courseId: course.id }
          : { categoryName: tutorials.related.categoryName }),
      });
    }

    async function shareInviteLink() {
      try {
        const invite = viewer.currentUser?.invite.hub;

        const inviteLink = buildFullLink({
          screen: screens.SwitchNavigator,
          params: {
            screen: screens.AuthNavigator,
            params: {
              screen: screens.Referral,
              params: { invite },
            },
          },
        });

        const link = await viewer.createShortLink.run({
          link: inviteLink,
        });

        if (!link) {
          throw new Error(t('errors.sharedLink'));
        }

        await ShareService.shareUrl(
          { url: link.shortLink },
          {
            clipboardMessage: t('tutorial.linkCopied'),
          },
        );
      } catch (error) {
        const message = (error as Error).message;
        ToastService.showError(message ?? t('errors.default'));
      }
    }

    async function fetchSubtitles(
      language: SwitchSubtitlesLanguageType,
    ) {
      try {
        if (!language || !!tutorial.subtitles[language]) {
          return;
        }

        await tutorial.subtitles.fetch.run(language);
      } catch (error) {
        // Do nothing
      }
    }

    // TODO: may find a way to connect this thunk to AnalyticsService
    async function saveWatchTime() {
      try {
        await tutorial.saveWatchTime.run({
          ms: playback.position,
          id: tutorial.id,
        });
      } catch (error) {
        // Do nothing
      }
    }

    function savePosition() {
      if (!playback.isLoaded) {
        return;
      }

      if (playback.position === playback.duration) {
        app.videoPlayerMemory.resetItem(tutorial.id);
      } else {
        app.videoPlayerMemory.setItem({
          id: tutorial.id,
          position: playback.position,
        });
      }
    }

    async function saveView() {
      try {
        if (store.didViewSaved) {
          return;
        }

        await tutorial.saveView.run();
        store.markViewAsSaved();
        app.incrementViewedTutorials();

        trackContentPlayAnalytic();
      } catch (error) {
        // Do nothing
      }
    }

    async function saveCurrentTutorial() {
      try {
        if (course) {
          await course.setCurrentTutorial.run(tutorial.id);
        }
      } catch (error) {
        // Do nothing
      }
    }

    async function trackCourseLessonStarted() {
      try {
        if (!course) {
          return;
        }

        const lesson = course.lessons.getLessonByTutorialId(
          tutorial.id,
        );

        if (lesson && !lesson.userStats?.isStarted) {
          await lesson.start.run();

          AnalyticsService.trackCourseLessonStarted({
            courseId: course.id,
            lessonId: lesson.id,
            lessonTitle: lesson.name,
          });
        }
      } catch (error) {
        // Do nothing
      }
    }

    function onPlayStart() {
      store.initiatePlayback();
      saveView();
      saveCurrentTutorial();

      if (course) {
        trackCourseLessonStarted();
      }
    }

    function navigateToTutorial(
      item: Tutorial,
      extraParams?: RouteExtraParamsType,
    ) {
      nav.replace(screens.Tutorial, {
        ...extraParams,
        id: item.id,
        ...(course
          ? { courseId: course.id }
          : {
              extraCategoryId: route.params.extraCategoryId,
            }),

        // We are not using this prop in WEB, passIf prevent appearing this
        // prop in the address path as query prop
        isInFullScreen: passIf(
          IS_MOBILE,
          store.isVisibleFullScreenPlayer,
        ),
      });
    }

    function navigateToNextVideo() {
      const nextItem = playlist.nextItem(tutorial.id);

      if (nextItem && nextItem.isAvailableForCurrentPlan) {
        navigateToTutorial(nextItem, {
          shouldPlay: true,
        });
      }
    }

    function navigateToPreviousVideo() {
      const previousItem = playlist.previousItem(tutorial.id);

      if (previousItem && previousItem.isAvailableForCurrentPlan) {
        navigateToTutorial(previousItem);
      }
    }

    function switchFullScreen(playerStatus: AVPlaybackStatus) {
      store.switchFullScreen(playerStatus);
    }

    async function trackCourseLessonCompleted() {
      try {
        if (!course) {
          return;
        }

        const lesson = course.lessons.getLessonByTutorialId(
          tutorial.id,
        );

        if (lesson && !lesson.userStats?.isCompleted) {
          const hasAllTutorialsWatched = lesson.tutorials.every(
            (i: Tutorial) => i.userStats?.isWatched,
          );

          if (hasAllTutorialsWatched) {
            await lesson.complete.run();

            AnalyticsService.trackCourseLessonCompleted({
              courseId: course.id,
              lessonId: lesson.id,
              lessonTitle: lesson.name,
            });
          }
        }
      } catch (error) {
        // Do nothing
      }
    }

    async function onVideoFinish() {
      try {
        if (course) {
          if (!tutorial.userStats?.isWatched) {
            await course.lessons.markTutorialAsDone.run(tutorial.id);
          }

          trackCourseLessonCompleted();

          if (
            !course.userStats?.isCompleted &&
            course.isAllVideosWatched
          ) {
            await course.complete.run();

            AnalyticsService.trackCourseCompleted({
              courseId: course.id,
              courseTitle: course.title,
            });

            return openModal(modals.AlertModal, {
              title: t('tutorial.wellDone'),
              subtitle: course
                ? t('tutorial.allCourseVideosFinished')
                : t('tutorial.allVideosFinished'),
            });
          }
        }

        if (app.isEveryFiveTutorialsViewed) {
          return openModal(modals.InviteSuggestionModal, {
            onPress: shareInviteLink,
          });
        }

        navigateToNextVideo();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    function onPlaybackStatusUpdate(status: AVPlaybackStatus) {
      if (status.isLoaded) {
        // On WEB isLoaded is always true, to check if video is loaded we can use
        // durationMillis, when it is NaN this means that video is not loaded
        if (IS_WEB && Number.isNaN(status.durationMillis)) {
          return;
        }

        store.setIsShouldPlay(status.shouldPlay);

        playback.isLoaded = true;
        playback.position = status.positionMillis;
        playback.duration = status.durationMillis ?? 0;
      }
    }

    async function refreshToken() {
      if (auth.renewTokens.inProgress) {
        return when(() => auth.renewTokens.inProgress);
      }

      try {
        await auth.renewTokens.run();
      } catch (error) {
        // Do nothing
      }
    }

    function onLoad() {
      const position = app.videoPlayerMemory.getItem(
        tutorial.id,
      )?.position;

      if (position) {
        getActivePlayerRef()?.setPositionAsync(position);
      }
    }

    const source = useMemo<AVPlaybackSource>(
      () => ({
        uri: tutorial.streamSource as string,
        overrideFileExtensionAndroid: 'm3u8',
      }),
      [tutorial.id],
    );

    const hasNextVideo =
      !!playlist.nextItem(tutorial.id) &&
      !!playlist.nextItem(tutorial.id)?.isAvailableForCurrentPlan;

    const hasPreviousVideo =
      !!playlist.previousItem(tutorial.id) &&
      !!playlist.previousItem(tutorial.id)?.isAvailableForCurrentPlan;

    return (
      <Container>
        <FullScreenVideoPlayer
          keyId={tutorial.id}
          isVisibleFullScreenPlayer={isVisibleFullScreenPlayer}
          videoPlayerRef={videoPlayerRef}
          fullScreenVideoPlayerRef={fullScreenVideoPlayerRef}
          source={source}
          hasNextVideo={hasNextVideo}
          hasPreviousVideo={hasPreviousVideo}
          onToggleFullScreen={switchFullScreen}
          onNextVideo={navigateToNextVideo}
          onPreviousVideo={navigateToPreviousVideo}
          onSwitchSubtitles={fetchSubtitles}
          subtitles={tutorial.subtitles}
          onPlayStart={onPlayStart}
          onVideoFinish={onVideoFinish}
          timestamps={tutorial.timestampsAsArray}
          onPressTimestamp={onPressTimestamp}
          onPlaybackStatusUpdate={onPlaybackStatusUpdate}
          onRefreshTokens={refreshToken}
          onLoad={onLoad}
        />
      </Container>
    );
  },
);
